import { Injectable, inject } from '@angular/core';
import { Store } from '@ngrx/store';
import { OidcSecurityService, OpenIdConfiguration } from 'angular-auth-oidc-client';
import { Observable, combineLatest, map, of, switchMap, tap } from 'rxjs';

import { Portal } from '@mobi/mcs-schadenmeldung-types-ng-jslib';
import { ConfigurationService } from '@mobi/rwc-utils-ng-jslib';

import * as fromCore from '../../state/selectors/core.selectors';

import { LogoutIntegrationService } from './logout-integration.service';

@Injectable({
    providedIn: 'root',
})
export class LogoutService {
    private readonly oidcSecurityService = inject(OidcSecurityService);
    private readonly logoutIntegrationService = inject(LogoutIntegrationService);
    private readonly configurationService = inject(ConfigurationService);
    private readonly store = inject(Store);

    public logout(): Observable<string | null> {
        return combineLatest([
            this.oidcSecurityService.getConfiguration(),
            this.oidcSecurityService.getRefreshToken(),
            this.store.select(fromCore.selectIsPublicAndPortal),
        ]).pipe(
            switchMap(([config, refreshToken, { isPublicZone, portal }]) => {
                this.oidcSecurityService.logoffLocal();
                return isPublicZone
                    ? this.getPublicLogoutUrl(portal)
                    : this.logoutIntegrationService.revokeRefreshToken(config, refreshToken).pipe(
                          switchMap(() => this.redirectToLogout(config)),
                          tap(logoutURL => {
                              if (logoutURL) {
                                  location.href = logoutURL;
                              }
                          }),
                      );
            }),
        );
    }

    private getPublicLogoutUrl(portal: Portal) {
        if (portal !== 'protekta') {
            const logoutUrl = this.configurationService.getSync('mobiLogoutDefaultUrl') as string;
            location.href = logoutUrl;
            return of(logoutUrl);
        }

        const logoutUrl = this.configurationService.getSync('protektaLogoutDefaultUrl') as string;
        location.href = logoutUrl;
        return of(logoutUrl);
    }

    private redirectToLogout(config: OpenIdConfiguration): Observable<string | null> {
        return this.oidcSecurityService.getAuthorizeUrl().pipe(
            map(url => {
                const authorizationEndpoint = config.authWellknownEndpoints?.authorizationEndpoint;
                const endSessionEndpoint = config.authWellknownEndpoints?.endSessionEndpoint;
                if (authorizationEndpoint && endSessionEndpoint && url) {
                    url = url.replace(authorizationEndpoint, endSessionEndpoint);
                }
                return url;
            }),
        );
    }
}
